import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Tooltip,
  InputAdornment,
  Checkbox,
  Chip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Error as ErrorIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/api';
import { getCurrentMonthYearBrasilia, formatDateTimeBR } from '../utils/dateUtils';

interface InternalError {
  id: string;
  orderNumber: string;
  department: string;
  responsible: string;
  responsibleUserId?: string | null;
  description: string;
  errorValue: string;
  createdAt: string;
  updatedAt: string;
}

interface NewInternalError {
  orderNumber: string;
  department: string;
  responsible: string;
  responsibleUserId?: string | null;
  description: string;
  errorValue: string;
}

interface UserOption {
  id: string;
  name: string;
  role: string;
  isActive?: boolean;
}

type PeriodType = 'month' | 'day';

const InternalErrors: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { month: currentMonth, year: currentYear } = getCurrentMonthYearBrasilia();
  const today = new Date().toISOString().slice(0, 10);
  const [errors, setErrors] = useState<InternalError[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingError, setEditingError] = useState<InternalError | null>(null);
  const [selectedErrors, setSelectedErrors] = useState<Set<string>>(new Set());
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info',
  });
  const [stats, setStats] = useState({
    count: 0,
    totalValue: '0.00',
    byDepartment: {},
    byResponsible: {},
    filters: {
      department: 'ALL',
      responsible: 'ALL',
      month: null,
      year: null,
    },
  });

  const [periodType, setPeriodType] = useState<PeriodType>('month');
  const [month, setMonth] = useState(currentMonth.toString());
  const [year, setYear] = useState(currentYear.toString());
  const [selectedDate, setSelectedDate] = useState(today);

  const [newErrorData, setNewErrorData] = useState<NewInternalError>({
    orderNumber: '',
    department: 'DESIGNER',
    responsible: '',
    responsibleUserId: null,
    description: '',
    errorValue: '',
  });

  const [users, setUsers] = useState<UserOption[]>([]);

  const canEdit = currentUser?.role === 'ADMIN' || currentUser?.role === 'SUPPORT' || currentUser?.role === 'MANAGER';
  // Quando o usuário NÃO tem permissão de gestão, vê apenas os próprios erros
  // (Designer/Vendedor/Produção): visão simplificada, somente leitura.
  const isOwnView = !canEdit;

  // Formata número (ou string) como moeda brasileira "R$ 1.234,56"
  const formatCurrencyBR = (value: number | string): string => {
    const n = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(n)) return 'R$ 0,00';
    return n.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Rótulo amigável do período atual ("abril/2026" ou "28/04/2026")
  const periodLabel = (() => {
    if (periodType === 'day') {
      const [y, m, d] = selectedDate.split('-');
      return `${d}/${m}/${y}`;
    }
    const monthNames = [
      'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
      'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
    ];
    return `${monthNames[parseInt(month) - 1]}/${year}`;
  })();

  const getDateRange = (): { startDate: string; endDate: string } => {
    if (periodType === 'day') {
      return { startDate: selectedDate, endDate: selectedDate };
    }
    const m = parseInt(month);
    const y = parseInt(year);
    const startDate = `${y}-${String(m).padStart(2, '0')}-01`;
    const lastDay = new Date(y, m, 0).getDate();
    const endDate = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    return { startDate, endDate };
  };

  const loadErrors = async () => {
    try {
      setLoading(true);
      const { startDate, endDate } = getDateRange();
      const data = await apiService.getAllInternalErrors(
        startDate,
        endDate,
        isOwnView ? { mine: true } : undefined,
      );
      setErrors(data);
    } catch (error: any) {
      showSnackbar('Erro ao carregar erros internos', 'error');
      console.error('Erro ao carregar erros internos:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async (department?: string) => {
    // Estatísticas globais existem apenas para perfis de gestão
    if (isOwnView) return;
    try {
      const { startDate, endDate } = getDateRange();
      const data = await apiService.getInternalErrorStats(
        department,
        undefined,
        startDate,
        endDate
      );
      setStats(data);
    } catch (error: any) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  useEffect(() => {
    loadErrors();
    loadStats();
  }, [periodType, month, year, selectedDate]);

  // Carrega lista de usuários para o select de "Responsável".
  // Usa /users/selectable, que é liberado para qualquer usuário autenticado
  // (a rota /users completa é restrita a ADMIN/MANAGER e bloquearia o Suporte).
  // Não carrega para perfis sem permissão de edição (visão "Meus Erros").
  useEffect(() => {
    if (isOwnView) return;
    (async () => {
      try {
        const list = await apiService.getSelectableUsers();
        const filtered = (list || [])
          .filter((u: any) => u?.isActive !== false)
          .map((u: any) => ({ id: u.id, name: u.name, role: u.role, isActive: u.isActive }));
        setUsers(filtered);
      } catch (err) {
        console.error('Erro ao carregar usuários:', err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadStats(departmentFilter || undefined);
  }, [departmentFilter, errors]);

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Adicionar novo erro
  const handleAddError = async () => {
    try {
      if (!newErrorData.orderNumber || !newErrorData.department || !newErrorData.responsible || !newErrorData.description || !newErrorData.errorValue) {
        showSnackbar('Preencha todos os campos obrigatórios', 'warning');
        return;
      }

      await apiService.createInternalError(newErrorData);
      showSnackbar('Erro interno adicionado com sucesso!', 'success');
      setOpenAddDialog(false);
      setNewErrorData({
        orderNumber: '',
        department: 'DESIGNER',
        responsible: '',
        responsibleUserId: null,
        description: '',
        errorValue: '',
      });
      loadErrors();
    } catch (error: any) {
      showSnackbar('Erro ao adicionar erro interno', 'error');
      console.error('Erro ao adicionar erro interno:', error);
    }
  };

  // Editar erro
  const handleEditClick = (error: InternalError) => {
    setEditingError(error);
    setOpenEditDialog(true);
  };

  const handleUpdateError = async () => {
    if (!editingError) return;

    try {
      await apiService.updateInternalError(editingError.id, {
        orderNumber: editingError.orderNumber,
        department: editingError.department,
        responsible: editingError.responsible,
        responsibleUserId: editingError.responsibleUserId ?? null,
        description: editingError.description,
        errorValue: editingError.errorValue,
      });

      showSnackbar('Erro interno atualizado com sucesso!', 'success');
      setOpenEditDialog(false);
      setEditingError(null);
      loadErrors();
    } catch (error: any) {
      showSnackbar('Erro ao atualizar erro interno', 'error');
      console.error('Erro ao atualizar erro interno:', error);
    }
  };

  // Deletar erro
  const handleDeleteError = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja deletar este erro interno?')) {
      return;
    }

    try {
      await apiService.deleteInternalError(id);
      showSnackbar('Erro interno deletado com sucesso!', 'success');
      loadErrors();
    } catch (error: any) {
      showSnackbar('Erro ao deletar erro interno', 'error');
      console.error('Erro ao deletar erro interno:', error);
    }
  };

  // Deletar múltiplos erros
  const handleDeleteMultiple = async () => {
    if (selectedErrors.size === 0) {
      showSnackbar('Selecione pelo menos um erro interno', 'warning');
      return;
    }

    if (!window.confirm(`Tem certeza que deseja deletar ${selectedErrors.size} erros internos?`)) {
      return;
    }

    try {
      await apiService.deleteMultipleInternalErrors(Array.from(selectedErrors));
      showSnackbar(`${selectedErrors.size} erros internos deletados com sucesso!`, 'success');
      setSelectedErrors(new Set());
      loadErrors();
    } catch (error: any) {
      showSnackbar('Erro ao deletar erros internos', 'error');
      console.error('Erro ao deletar erros internos:', error);
    }
  };

  // Seleção de múltiplos
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allIds = new Set(filteredErrors.map(error => error.id));
      setSelectedErrors(allIds);
    } else {
      setSelectedErrors(new Set());
    }
  };

  const handleSelectOne = (id: string) => {
    const newSelected = new Set(selectedErrors);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedErrors(newSelected);
  };

  // Filtros
  const filteredErrors = errors.filter(error => {
    const matchesSearch = 
      error.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      error.responsible.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesDepartment = !departmentFilter || error.department === departmentFilter;

    return matchesSearch && matchesDepartment;
  });

  // Mapear departamentos para exibição
  const departmentMap: { [key: string]: string } = {
    'DESIGNER': 'Designer',
    'COMERCIAL': 'Comercial',
    'EXPEDICAO': 'Expedição',
    'PRODUCAO': 'Produção',
  };

  const departmentOptions = ['DESIGNER', 'COMERCIAL', 'EXPEDICAO', 'PRODUCAO'];

  // Cores por departamento
  const departmentColors: { [key: string]: string } = {
    'DESIGNER': '#ec4899',
    'COMERCIAL': '#10b981',
    'EXPEDICAO': '#f59e0b',
    'PRODUCAO': '#3b82f6',
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 1 }}>
            {isOwnView ? 'Meus Erros Internos' : 'Erros Internos'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            {isOwnView
              ? 'Erros internos registrados em seu nome'
              : 'Gerencie e acompanhe erros internos por setor e responsável'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Período</InputLabel>
            <Select
              value={periodType}
              onChange={(e) => setPeriodType(e.target.value as PeriodType)}
              label="Período"
            >
              <MenuItem value="month">Mês inteiro</MenuItem>
              <MenuItem value="day">Dia específico</MenuItem>
            </Select>
          </FormControl>

          {periodType === 'month' && (
            <>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Mês</InputLabel>
                <Select value={month} onChange={(e) => setMonth(e.target.value)} label="Mês">
                  <MenuItem value="1">Janeiro</MenuItem>
                  <MenuItem value="2">Fevereiro</MenuItem>
                  <MenuItem value="3">Março</MenuItem>
                  <MenuItem value="4">Abril</MenuItem>
                  <MenuItem value="5">Maio</MenuItem>
                  <MenuItem value="6">Junho</MenuItem>
                  <MenuItem value="7">Julho</MenuItem>
                  <MenuItem value="8">Agosto</MenuItem>
                  <MenuItem value="9">Setembro</MenuItem>
                  <MenuItem value="10">Outubro</MenuItem>
                  <MenuItem value="11">Novembro</MenuItem>
                  <MenuItem value="12">Dezembro</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Ano</InputLabel>
                <Select value={year} onChange={(e) => setYear(e.target.value)} label="Ano">
                  <MenuItem value="2023">2023</MenuItem>
                  <MenuItem value="2024">2024</MenuItem>
                  <MenuItem value="2025">2025</MenuItem>
                  <MenuItem value="2026">2026</MenuItem>
                </Select>
              </FormControl>
            </>
          )}

          {periodType === 'day' && (
            <TextField
              size="small"
              label="Data"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
          )}

          {canEdit && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenAddDialog(true)}
              sx={{
                backgroundColor: '#D4AF37',
                color: '#000',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                '&:hover': {
                  backgroundColor: '#B8941F',
                },
              }}
            >
              Adicionar Erro
            </Button>
          )}
        </Box>
      </Box>

      {/* Estatísticas - apenas para perfis de gestão */}
      {!isOwnView && (
      <Card sx={{ mb: 3, backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                Total de Erros
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                {stats.count}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                Valor Total ({periodLabel})
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#dc2626' }}>
                {formatCurrencyBR(stats.totalValue)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                Setor com Mais Erros
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                {Object.keys(stats.byDepartment).length > 0
                  ? (() => {
                      const departments = Object.keys(stats.byDepartment);
                      const sorted = departments.sort((a, b) => {
                        const deptA = (stats.byDepartment as any)[a];
                        const deptB = (stats.byDepartment as any)[b];
                        
                        // Primeiro compara pelo valor total
                        if (deptB.total !== deptA.total) {
                          return deptB.total - deptA.total;
                        }
                        
                        // Se os valores forem iguais, compara pela quantidade
                        return deptB.count - deptA.count;
                      });
                      return departmentMap[sorted[0]];
                    })()
                  : '-'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                Filtro Ativo
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#D4AF37' }}>
                {departmentFilter ? departmentMap[departmentFilter] : 'Todos'}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      )}

      {/* Filtros - busca completa só na visão de gestão */}
      {!isOwnView ? (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Buscar por número de pedido ou responsável"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Setor</InputLabel>
                  <Select
                    value={departmentFilter}
                    onChange={(e) => setDepartmentFilter(e.target.value)}
                    label="Setor"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {departmentOptions.map(dept => (
                      <MenuItem key={dept} value={dept}>
                        {departmentMap[dept]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={loadErrors}
                  sx={{
                    borderColor: '#D4AF37',
                    color: '#D4AF37',
                    height: '56px',
                    '&:hover': {
                      borderColor: '#B8941F',
                      backgroundColor: 'rgba(212, 175, 55, 0.04)',
                    },
                  }}
                >
                  Atualizar
                </Button>
              </Grid>
              {canEdit && selectedErrors.size > 0 && (
                <Grid item xs={12} md={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleDeleteMultiple}
                    sx={{ height: '56px' }}
                  >
                    Deletar ({selectedErrors.size})
                  </Button>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      ) : (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  placeholder="Buscar por número de pedido"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={loadErrors}
                  sx={{
                    borderColor: '#D4AF37',
                    color: '#D4AF37',
                    height: '56px',
                    '&:hover': {
                      borderColor: '#B8941F',
                      backgroundColor: 'rgba(212, 175, 55, 0.04)',
                    },
                  }}
                >
                  Atualizar
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Tabela */}
      <Card>
        <TableContainer sx={{ overflowX: 'auto', width: '100%', display: 'block' }}>
          <Table sx={{ width: '100%', tableLayout: 'auto' }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(212, 175, 55, 0.05)', height: 70, '& > th': { py: 3 } }}>
                {canEdit && (
                  <TableCell sx={{ width: 50, textAlign: 'center', px: 1 }}>
                    <Checkbox
                      checked={selectedErrors.size === filteredErrors.length && filteredErrors.length > 0}
                      indeterminate={selectedErrors.size > 0 && selectedErrors.size < filteredErrors.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                )}
                <TableCell sx={{ fontWeight: 600, color: '#D4AF37', px: 1, flex: 1 }}>Nº PEDIDO</TableCell>
                {!isOwnView && (
                  <TableCell sx={{ fontWeight: 600, color: '#D4AF37', px: 1, flex: 1 }}>SETOR RESPONSÁVEL</TableCell>
                )}
                {!isOwnView && (
                  <TableCell sx={{ fontWeight: 600, color: '#D4AF37', px: 1, flex: 1 }}>RESPONSÁVEL</TableCell>
                )}
                <TableCell sx={{ fontWeight: 600, color: '#D4AF37', px: 1, flex: 1 }}>OCORRÊNCIA</TableCell>
                {!isOwnView && (
                  <TableCell sx={{ fontWeight: 600, color: '#D4AF37', px: 1, flex: 1 }}>VALOR DO ERRO</TableCell>
                )}
                {isOwnView && (
                  <TableCell sx={{ fontWeight: 600, color: '#D4AF37', px: 1, flex: 1 }}>DATA</TableCell>
                )}
                {canEdit && (
                  <TableCell sx={{ fontWeight: 600, color: '#D4AF37', px: 1, textAlign: 'center', flex: 1 }}>AÇÕES</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={canEdit ? 7 : (isOwnView ? 3 : 5)} align="center" sx={{ py: 4 }}>
                    Carregando...
                  </TableCell>
                </TableRow>
              ) : filteredErrors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canEdit ? 7 : (isOwnView ? 3 : 5)} align="center" sx={{ py: 4 }}>
                    Nenhum erro interno encontrado
                  </TableCell>
                </TableRow>
              ) : (
                filteredErrors.map((error) => (
                  <TableRow
                    key={error.id}
                    sx={{
                      '&:hover': { backgroundColor: 'rgba(212, 175, 55, 0.02)' },
                      height: 70,
                    }}
                  >
                    {canEdit && (
                      <TableCell sx={{ textAlign: 'center', px: 1 }}>
                        <Checkbox
                          checked={selectedErrors.has(error.id)}
                          onChange={() => handleSelectOne(error.id)}
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ px: 1, flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {error.orderNumber}
                      </Typography>
                    </TableCell>
                    {!isOwnView && (
                      <TableCell sx={{ px: 1, flex: 1 }}>
                        <Chip
                          icon={<BusinessIcon />}
                          label={departmentMap[error.department] || error.department}
                          size="small"
                          sx={{
                            backgroundColor: departmentColors[error.department] || '#666',
                            color: '#fff',
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                    )}
                    {!isOwnView && (
                      <TableCell sx={{ px: 1, flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon sx={{ fontSize: 18, color: '#666' }} />
                          <Typography variant="body2">
                            {error.responsible}
                          </Typography>
                        </Box>
                      </TableCell>
                    )}
                    <TableCell sx={{ px: 1, flex: 1 }}>
                      {isOwnView ? (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {error.description}
                        </Typography>
                      ) : (
                        <Tooltip title={error.description}>
                          <Typography variant="body2" sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {error.description}
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    {!isOwnView && (
                      <TableCell sx={{ px: 1, flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#dc2626' }}>
                          R$ {error.errorValue}
                        </Typography>
                      </TableCell>
                    )}
                    {isOwnView && (
                      <TableCell sx={{ px: 1, flex: 1 }}>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          {formatDateTimeBR(error.createdAt)}
                        </Typography>
                      </TableCell>
                    )}
                    {canEdit && (
                      <TableCell sx={{ px: 1, textAlign: 'center', flex: 1 }}>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => handleEditClick(error)}
                            sx={{ color: '#D4AF37', mr: 1 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Deletar">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteError(error.id)}
                            sx={{ color: '#ef4444' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Dialog: Adicionar Erro */}
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#D4AF37', color: '#000', fontWeight: 600 }}>
          Adicionar Novo Erro Interno
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Número do Pedido"
                value={newErrorData.orderNumber}
                onChange={(e) => setNewErrorData({ ...newErrorData, orderNumber: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Setor Responsável</InputLabel>
                <Select
                  value={newErrorData.department}
                  onChange={(e) => setNewErrorData({ ...newErrorData, department: e.target.value })}
                  label="Setor Responsável"
                >
                  {departmentOptions.map(dept => (
                    <MenuItem key={dept} value={dept}>
                      {departmentMap[dept]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Responsável</InputLabel>
                <Select
                  value={newErrorData.responsibleUserId || ''}
                  label="Responsável"
                  onChange={(e) => {
                    const id = e.target.value as string;
                    const u = users.find(x => x.id === id);
                    setNewErrorData({
                      ...newErrorData,
                      responsibleUserId: id || null,
                      responsible: u?.name || '',
                    });
                  }}
                >
                  <MenuItem value="">
                    <em>Selecione um usuário</em>
                  </MenuItem>
                  {users.map(u => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.name} ({u.role})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Valor do Erro"
                value={newErrorData.errorValue}
                onChange={(e) => setNewErrorData({ ...newErrorData, errorValue: e.target.value })}
                placeholder="0.00"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Ocorrência (Descrição)"
                value={newErrorData.description}
                onChange={(e) => setNewErrorData({ ...newErrorData, description: e.target.value })}
                multiline
                rows={3}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenAddDialog(false)} sx={{ color: '#666' }}>
            Cancelar
          </Button>
          <Button
            onClick={handleAddError}
            variant="contained"
            sx={{
              backgroundColor: '#D4AF37',
              color: '#000',
              '&:hover': { backgroundColor: '#B8941F' },
            }}
          >
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Editar Erro */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#D4AF37', color: '#000', fontWeight: 600 }}>
          Editar Erro Interno
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {editingError && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Número do Pedido"
                  value={editingError.orderNumber}
                  onChange={(e) => setEditingError({ ...editingError, orderNumber: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Setor Responsável</InputLabel>
                  <Select
                    value={editingError.department}
                    onChange={(e) => setEditingError({ ...editingError, department: e.target.value })}
                    label="Setor Responsável"
                  >
                    {departmentOptions.map(dept => (
                      <MenuItem key={dept} value={dept}>
                        {departmentMap[dept]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Responsável</InputLabel>
                  <Select
                    value={editingError.responsibleUserId || ''}
                    label="Responsável"
                    onChange={(e) => {
                      const id = e.target.value as string;
                      const u = users.find(x => x.id === id);
                      setEditingError({
                        ...editingError,
                        responsibleUserId: id || null,
                        responsible: u?.name || editingError.responsible,
                      });
                    }}
                  >
                    <MenuItem value="">
                      <em>{editingError.responsible || 'Selecione um usuário'}</em>
                    </MenuItem>
                    {users.map(u => (
                      <MenuItem key={u.id} value={u.id}>
                        {u.name} ({u.role})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Valor do Erro"
                  value={editingError.errorValue}
                  onChange={(e) => setEditingError({ ...editingError, errorValue: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Ocorrência (Descrição)"
                  value={editingError.description}
                  onChange={(e) => setEditingError({ ...editingError, description: e.target.value })}
                  multiline
                  rows={3}
                  required
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenEditDialog(false)} sx={{ color: '#666' }}>
            Cancelar
          </Button>
          <Button
            onClick={handleUpdateError}
            variant="contained"
            sx={{
              backgroundColor: '#D4AF37',
              color: '#000',
              '&:hover': { backgroundColor: '#B8941F' },
            }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InternalErrors;
